import { Alert, Box, Table, TableBody, TableHead, TableRow } from '@mui/material';

import {
  ConfirmationDialog,
  GeneralTableBox,
  HeadTableCell,
  NoDataSkeleton,
  PaginationTable,
  RowTableCell,
  SortableHeadTableCell,
  TableBodySkeleton,
} from '../../../common/components';
import { formatDateTime } from '../../../utils/formatUtils';
import {
  RETURN_TABLE_COLUMNS as COLUMNS,
  RETURN_TABLE_LAYOUT,
  RETURN_TEXTS as TEXTS,
} from '../constants/consumptionReturnTexts';
import ConsumptionReturnActions from './ConsumptionReturnActions';
import ConsumptionReturnCellValue from './ConsumptionReturnCellValue';
import ConsumptionReturnsToolbar from './ConsumptionReturnsToolbar';
import { getReturnCellSx } from '../utils/getReturnCellSx';

/**
 * Pestaña Devoluciones de obra: filtros, listado con autorización, recepción y anulación.
 * Recibe todo por props desde `useConsumptionReturnsPanel` (lo llama la página).
 * @param {Object} props
 * @param {Array<Object>} props.rows Devoluciones de la página visible.
 * @param {number} props.totalCount Total filtrado.
 * @param {number} props.page Página actual.
 * @param {Function} props.onPageChange Cambia de página.
 * @param {{key: string, direction: string}|null} props.sort Orden vigente.
 * @param {Function} props.onSort Ordena por una columna.
 * @param {string} props.search Texto buscado.
 * @param {Function} props.onSearch Cambia la búsqueda.
 * @param {Object} props.filters Filtros de obra y estado.
 * @param {Function} props.onFilter Cambia un filtro.
 * @param {Function} props.onClear Limpia filtros y búsqueda.
 * @param {Array<Object>} props.projects Viviendas para el filtro.
 * @param {boolean} props.canAuthorize Si el usuario autoriza y anula devoluciones.
 * @param {Function} props.onCreate Abre el alta.
 * @param {Function} props.onView Abre una devolución.
 * @param {Function} props.onCancelRequest Pide confirmación para anular.
 * @param {Object|null} props.cancelTarget Devolución a anular.
 * @param {boolean} props.isCancelling Anulación en curso.
 * @param {Function} props.onCancelConfirm Confirma la anulación.
 * @param {Function} props.onCancelClose Cierra la confirmación.
 * @param {boolean} props.isLoading Carga en curso.
 * @param {string} props.loadError Error de carga.
 * @param {string} props.actionError Error de la última acción.
 * @param {Function} props.onClearActionError Descarta el error de la acción.
 * @returns {JSX.Element}
 */
const ConsumptionReturnsPanel = ({
  rows,
  totalCount,
  page,
  rowsPerPage,
  onRowsPerPageChange,
  onPageChange,
  sort,
  onSort,
  search,
  onSearch,
  filters,
  onFilter,
  onClear,
  projects,
  canAuthorize,
  onCreate,
  onView,
  onCancelRequest,
  cancelTarget,
  isCancelling,
  onCancelConfirm,
  onCancelClose,
  isLoading,
  loadError,
  actionError,
  onClearActionError,
}) => (
  <Box>
    {loadError && <Alert severity="error">{loadError}</Alert>}
    {actionError && (
      <Alert severity="error" onClose={onClearActionError}>
        {actionError}
      </Alert>
    )}
    <ConsumptionReturnsToolbar
      canAuthorize={canAuthorize}
      onCreate={onCreate}
      search={search}
      onSearch={onSearch}
      filters={filters}
      onFilter={onFilter}
      onClear={onClear}
      projects={projects}
    />
    <GeneralTableBox fixedLayout minTableWidth={RETURN_TABLE_LAYOUT.minWidth}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {COLUMNS.map((column) => (
              <SortableHeadTableCell
                key={column.key}
                sortKey={column.key}
                sort={sort}
                onSort={onSort}
                sx={{
                  width: column.width,
                  textAlign: column.align || 'left',
                  px: 1.5,
                  fontSize: 13,
                }}
              >
                {column.label}
              </SortableHeadTableCell>
            ))}
            <HeadTableCell sx={{ width: RETURN_TABLE_LAYOUT.actions, px: 1.5 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableBodySkeleton />
          ) : (
            rows.map((row) => (
              <TableRow key={row.uuid} hover>
                {COLUMNS.map((column) => (
                  <RowTableCell
                    key={column.key}
                    title={
                      column.key === 'created_at' ? formatDateTime(row.created_at) : row[column.key]
                    }
                    sx={getReturnCellSx(column)}
                  >
                    <ConsumptionReturnCellValue columnKey={column.key} row={row} />
                  </RowTableCell>
                ))}
                <RowTableCell sx={{ px: 1.5, verticalAlign: 'middle' }}>
                  <ConsumptionReturnActions
                    row={row}
                    canCancel={canAuthorize}
                    disabled={isCancelling}
                    onView={onView}
                    onCancel={onCancelRequest}
                  />
                </RowTableCell>
              </TableRow>
            ))
          )}
          {!isLoading && !rows.length && (
            <NoDataSkeleton colSpan={COLUMNS.length + 1} message={TEXTS.empty} />
          )}
        </TableBody>
      </Table>
    </GeneralTableBox>
    {totalCount > 0 && (
      <PaginationTable
        page={page}
        totalPages={Math.ceil(totalCount / rowsPerPage)}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        onPageChange={onPageChange}
      />
    )}
    <ConfirmationDialog
      open={Boolean(cancelTarget)}
      onClose={onCancelClose}
      onConfirm={onCancelConfirm}
      title={TEXTS.cancelTitle}
      message={TEXTS.cancelMessage(cancelTarget)}
      confirmText={TEXTS.cancelConfirm}
      confirmVariant="danger"
      isLoading={isCancelling}
    />
  </Box>
);

export default ConsumptionReturnsPanel;
